import { useState, useEffect, useCallback, useRef } from 'react';
import { useChat } from '../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  GripVertical,
  Plus,
  Trash2,
  X,
  Download,
  Save,
  Play,
  Eye,
  RefreshCw,
  Search,
  Filter,
  Columns,
  ArrowUpDown,
  Layers,
  ChevronRight,
  ChevronDown,
  Database,
  Users,
  CreditCard,
  Calculator,
  Tag,
  FileText,
  Table,
  BarChart3,
  Sparkles,
  Clock,
  FolderOpen,
  Check,
  AlertCircle,
  Loader2,
  Copy,
  Hash,
  Calendar,
  Type
} from 'lucide-react';
import axios from 'axios';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Category icons mapping
const CATEGORY_ICONS = {
  customer: Users,
  transaction: CreditCard,
  calculated: Calculator,
  segment: Tag
};

// Field type icons
const TYPE_ICONS = {
  string: Type,
  number: Hash,
  date: Calendar
};

// Draggable Field Chip Component
const DraggableField = ({ field, category, onDragStart, onDragEnd }) => {
  const TypeIcon = TYPE_ICONS[field.type] || Type;
  
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, field, category)}
      onDragEnd={onDragEnd}
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background border border-border/50 cursor-grab active:cursor-grabbing hover:border-primary/50 hover:bg-primary/5 transition-all group"
      data-testid={`field-${field.id}`}
    >
      <TypeIcon className="w-3.5 h-3.5 text-muted-foreground" />
      <span className="text-sm font-medium">{field.label}</span>
      <GripVertical className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 ml-auto" />
    </div>
  );
};

// Drop Zone Component
const DropZone = ({ 
  title, 
  icon: Icon, 
  items, 
  onDrop, 
  onRemove, 
  isDragOver, 
  onDragOver, 
  onDragLeave,
  emptyText,
  renderItem,
  color = "violet"
}) => {
  const colorClasses = {
    violet: "from-violet-500/10 to-purple-500/5 border-violet-500/30",
    pink: "from-pink-500/10 to-rose-500/5 border-pink-500/30",
    cyan: "from-cyan-500/10 to-teal-500/5 border-cyan-500/30",
    amber: "from-amber-500/10 to-orange-500/5 border-amber-500/30"
  };

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`relative rounded-xl border-2 border-dashed transition-all ${
        isDragOver 
          ? `border-${color}-500 bg-gradient-to-br ${colorClasses[color]} scale-[1.02]` 
          : 'border-border/50 hover:border-border'
      }`}
    >
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${color}-500/20`}>
            <Icon className={`w-4 h-4 text-${color}-500`} />
          </div>
          <h4 className="font-semibold text-sm">{title}</h4>
          {items.length > 0 && (
            <Badge variant="secondary" className="ml-auto">{items.length}</Badge>
          )}
        </div>
        
        {items.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            <p>{emptyText}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item, idx) => renderItem(item, idx, onRemove))}
          </div>
        )}
      </div>
    </div>
  );
};

// Column Item Component
const ColumnItem = ({ item, index, onRemove }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background border group">
    <div 
      className="w-2 h-2 rounded-full" 
      style={{ backgroundColor: item.categoryColor }}
    />
    <span className="text-sm flex-1">{item.label}</span>
    <Button 
      variant="ghost" 
      size="icon" 
      className="w-6 h-6 opacity-0 group-hover:opacity-100"
      onClick={() => onRemove(index)}
    >
      <X className="w-3 h-3" />
    </Button>
  </div>
);

// Filter Item Component with operators
const FilterItem = ({ item, index, onRemove, onUpdate, operators }) => {
  const typeOperators = operators[item.type] || operators.string;
  
  return (
    <div className="p-3 rounded-lg bg-background border space-y-2 group">
      <div className="flex items-center gap-2">
        <div 
          className="w-2 h-2 rounded-full" 
          style={{ backgroundColor: item.categoryColor }}
        />
        <span className="text-sm font-medium flex-1">{item.label}</span>
        <Button 
          variant="ghost" 
          size="icon" 
          className="w-6 h-6"
          onClick={() => onRemove(index)}
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
      <div className="flex gap-2">
        <Select 
          value={item.operator || typeOperators[0]?.id} 
          onValueChange={(v) => onUpdate(index, { ...item, operator: v })}
        >
          <SelectTrigger className="h-8 text-xs flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {typeOperators.map(op => (
              <SelectItem key={op.id} value={op.id}>{op.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input 
          className="h-8 text-xs flex-1"
          placeholder={item.type === 'date' ? 'YYYY-MM-DD or days' : 'Value'}
          value={item.value || ''}
          onChange={(e) => onUpdate(index, { ...item, value: e.target.value })}
        />
        {item.operator === 'between' && (
          <Input 
            className="h-8 text-xs w-24"
            placeholder="Max"
            value={item.value2 || ''}
            onChange={(e) => onUpdate(index, { ...item, value2: e.target.value })}
          />
        )}
      </div>
    </div>
  );
};

// Group By Item Component
const GroupByItem = ({ item, index, onRemove }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background border group">
    <Badge variant="outline" className="text-xs">{index + 1}</Badge>
    <span className="text-sm flex-1">{item.label}</span>
    <Button 
      variant="ghost" 
      size="icon" 
      className="w-6 h-6 opacity-0 group-hover:opacity-100"
      onClick={() => onRemove(index)}
    >
      <X className="w-3 h-3" />
    </Button>
  </div>
);

// Results Table Component
const ResultsTable = ({ data, columns }) => {
  if (!data?.rows?.length) return null;
  
  const displayColumns = columns.length > 0 
    ? columns.map(c => c.field_id || c.id)
    : Object.keys(data.rows[0]);
  
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full">
        <thead>
          <tr className="bg-muted/50">
            {displayColumns.map((col, idx) => (
              <th key={idx} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                {col.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="border-t hover:bg-muted/30">
              {displayColumns.map((col, colIdx) => (
                <td key={colIdx} className="px-4 py-3 text-sm">
                  {typeof row[col] === 'number' 
                    ? row[col].toLocaleString() 
                    : row[col] || '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Saved Reports Panel
const SavedReportsPanel = ({ reports, onLoad, onDelete, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-y-0 right-0 w-80 bg-background border-l shadow-xl z-50 flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <FolderOpen className="w-4 h-4" />
          Saved Reports
        </h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {reports.length === 0 ? (
          <p className="text-center text-muted-foreground text-sm py-8">
            No saved reports yet
          </p>
        ) : (
          reports.map((report, idx) => (
            <Card key={idx} className="hover:border-primary/50 cursor-pointer group">
              <CardContent className="p-3" onClick={() => onLoad(report)}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-sm">{report.name}</h4>
                    <p className="text-xs text-muted-foreground">
                      {new Date(report.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="w-6 h-6 opacity-0 group-hover:opacity-100"
                    onClick={(e) => { e.stopPropagation(); onDelete(report.name); }}
                  >
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default function ReportBuilder() {
  const { selectedBrand } = useChat();
  
  // Schema state
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [isLiveData, setIsLiveData] = useState(false);
  
  // Builder state
  const [reportName, setReportName] = useState('New Report');
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [sortBy, setSortBy] = useState({ field: '', desc: true });
  const [outputType, setOutputType] = useState('count');
  
  // Drag state
  const [draggedField, setDraggedField] = useState(null);
  const [dragOverZone, setDragOverZone] = useState(null);
  
  // Results state
  const [results, setResults] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);
  
  // Saved reports
  const [savedReports, setSavedReports] = useState([]);
  const [showSavedReports, setShowSavedReports] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Field palette state
  const [expandedCategories, setExpandedCategories] = useState(['customer', 'calculated', 'segment']);
  const [fieldSearch, setFieldSearch] = useState('');
  
  // Fetch schema on mount
  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const response = await axios.get(
          `${API}/api/report-builder/schema`,
          { withCredentials: true }
        );
        setSchema(response.data.data);
      } catch (err) {
        console.error('Schema fetch error:', err);
      } finally {
        setSchemaLoading(false);
      }
    };
    fetchSchema();
  }, []);
  
  // Fetch saved reports
  useEffect(() => {
    const fetchSavedReports = async () => {
      try {
        const response = await axios.get(
          `${API}/api/report-builder/saved`,
          { withCredentials: true }
        );
        setSavedReports(response.data.data || []);
      } catch (err) {
        console.error('Saved reports fetch error:', err);
      }
    };
    fetchSavedReports();
  }, []);
  
  // Drag handlers
  const handleDragStart = (e, field, categoryKey) => {
    const category = schema?.field_categories[categoryKey];
    setDraggedField({ 
      ...field, 
      field_id: field.id,
      categoryKey, 
      categoryColor: category?.color 
    });
    e.dataTransfer.effectAllowed = 'copy';
  };
  
  const handleDragEnd = () => {
    setDraggedField(null);
    setDragOverZone(null);
  };
  
  const handleDragOver = (e, zone) => {
    e.preventDefault();
    setDragOverZone(zone);
  };
  
  const handleDragLeave = () => {
    setDragOverZone(null);
  };
  
  const handleDrop = (e, zone) => {
    e.preventDefault();
    setDragOverZone(null);
    
    if (!draggedField) return;
    
    const newItem = { ...draggedField };
    
    switch (zone) {
      case 'columns':
        if (!columns.find(c => c.field_id === newItem.field_id)) {
          setColumns([...columns, newItem]);
        }
        break;
      case 'filters':
        setFilters([...filters, { ...newItem, operator: '', value: '' }]);
        break;
      case 'groupBy':
        if (!groupBy.find(g => g.field_id === newItem.field_id)) {
          setGroupBy([...groupBy, newItem]);
        }
        break;
      default:
        break;
    }
  };
  
  // Remove handlers
  const removeColumn = (index) => setColumns(columns.filter((_, i) => i !== index));
  const removeFilter = (index) => setFilters(filters.filter((_, i) => i !== index));
  const removeGroupBy = (index) => setGroupBy(groupBy.filter((_, i) => i !== index));
  
  // Update filter
  const updateFilter = (index, newFilter) => {
    const updated = [...filters];
    updated[index] = newFilter;
    setFilters(updated);
  };
  
  // Execute report
  const executeReport = async (preview = false) => {
    if (!selectedBrand) return;
    
    setExecuting(true);
    setPreviewMode(preview);
    
    try {
      const endpoint = preview ? '/api/report-builder/preview' : '/api/report-builder/execute';
      const response = await axios.post(
        `${API}${endpoint}`,
        {
          brand_id: selectedBrand.brand_id,
          columns: columns.map(c => ({ field_id: c.field_id })),
          filters: filters.map(f => ({ 
            field_id: f.field_id, 
            operator: f.operator, 
            value: f.value,
            value2: f.value2 
          })),
          group_by: groupBy.map(g => ({ field_id: g.field_id })),
          sort_by: sortBy,
          output_type: outputType,
          limit: preview ? 10 : 500
        },
        { withCredentials: true }
      );
      setResults(response.data.data);
    } catch (err) {
      console.error('Report execution error:', err);
    } finally {
      setExecuting(false);
    }
  };
  
  // Export CSV
  const exportCSV = async () => {
    if (!selectedBrand) return;
    
    try {
      const response = await axios.post(
        `${API}/api/report-builder/export-csv`,
        {
          brand_id: selectedBrand.brand_id,
          columns: columns.map(c => ({ field_id: c.field_id })),
          filters: filters.map(f => ({ 
            field_id: f.field_id, 
            operator: f.operator, 
            value: f.value,
            value2: f.value2 
          })),
          group_by: groupBy.map(g => ({ field_id: g.field_id })),
          sort_by: sortBy,
          output_type: outputType
        },
        { withCredentials: true, responseType: 'blob' }
      );
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };
  
  // Save report
  const saveReport = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/report-builder/save`,
        {
          name: reportName,
          config: { columns, filters, groupBy, sortBy, outputType }
        },
        { withCredentials: true }
      );
      
      // Refresh saved reports
      const response = await axios.get(
        `${API}/api/report-builder/saved`,
        { withCredentials: true }
      );
      setSavedReports(response.data.data || []);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };
  
  // Load saved report
  const loadReport = (report) => {
    setReportName(report.name);
    setColumns(report.config.columns || []);
    setFilters(report.config.filters || []);
    setGroupBy(report.config.groupBy || []);
    setSortBy(report.config.sortBy || { field: '', desc: true });
    setOutputType(report.config.outputType || 'count');
    setShowSavedReports(false);
    setResults(null);
  };
  
  // Delete saved report
  const deleteReport = async (name) => {
    try {
      await axios.delete(
        `${API}/api/report-builder/saved/${encodeURIComponent(name)}`,
        { withCredentials: true }
      );
      setSavedReports(savedReports.filter(r => r.name !== name));
    } catch (err) {
      console.error('Delete error:', err);
    }
  };
  
  // Clear all
  const clearAll = () => {
    setColumns([]);
    setFilters([]);
    setGroupBy([]);
    setSortBy({ field: '', desc: true });
    setOutputType('count');
    setResults(null);
    setReportName('New Report');
  };
  
  // Filter fields by search
  const filterFields = (fields) => {
    if (!fieldSearch) return fields;
    return fields.filter(f => 
      f.label.toLowerCase().includes(fieldSearch.toLowerCase())
    );
  };
  
  if (schemaLoading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="report-builder-loading">
        <div className="flex flex-col items-center gap-4">
          <Database className="w-16 h-16 text-violet-500 animate-pulse" />
          <p className="text-muted-foreground">Loading report builder...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-builder">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500 shadow-lg shadow-violet-500/30">
            <Layers className="w-6 h-6 text-white" />
          </div>
          <div>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              className="text-2xl font-bold bg-transparent border-none outline-none focus:ring-0 w-full"
              placeholder="Report Name"
              data-testid="report-name-input"
            />
            <p className="text-muted-foreground text-sm">Drag fields to build your custom cohort report</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowSavedReports(true)}
            className="gap-2"
          >
            <FolderOpen className="w-4 h-4" />
            Saved ({savedReports.length})
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearAll}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Clear
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={saveReport}
            disabled={saving}
            className="gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportCSV}
            disabled={!results?.rows?.length}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            CSV
          </Button>
        </div>
      </div>

      {/* Main Builder Area */}
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Field Palette - Left Side */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="sticky top-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Database className="w-4 h-4" />
                Available Fields
              </CardTitle>
              <div className="relative mt-2">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search fields..."
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto space-y-3">
              {schema && Object.entries(schema.field_categories).map(([key, category]) => {
                const Icon = CATEGORY_ICONS[key] || Database;
                const isExpanded = expandedCategories.includes(key);
                const filteredFields = filterFields(category.fields);
                
                if (fieldSearch && filteredFields.length === 0) return null;
                
                return (
                  <div key={key} className="space-y-2">
                    <button
                      onClick={() => setExpandedCategories(prev => 
                        prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]
                      )}
                      className="flex items-center gap-2 w-full text-left py-1 hover:text-primary transition-colors"
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="text-sm font-medium">{category.label}</span>
                      <Badge variant="outline" className="ml-auto text-xs">
                        {filteredFields.length}
                      </Badge>
                    </button>
                    
                    {isExpanded && (
                      <div className="space-y-1.5 pl-6">
                        {filteredFields.map(field => (
                          <DraggableField
                            key={field.id}
                            field={field}
                            category={key}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Builder Canvas - Center */}
        <div className="lg:col-span-2 space-y-4">
          {/* Output Type Selection */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm font-medium">Output Type:</span>
                {schema?.output_types?.map(type => (
                  <Button
                    key={type.id}
                    variant={outputType === type.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setOutputType(type.id)}
                    className="gap-2"
                  >
                    {type.id === 'count' && <Hash className="w-4 h-4" />}
                    {type.id === 'list' && <Table className="w-4 h-4" />}
                    {type.id === 'aggregate' && <BarChart3 className="w-4 h-4" />}
                    {type.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Drop Zones */}
          <div className="grid gap-4">
            {/* Columns Zone */}
            {outputType !== 'count' && (
              <DropZone
                title="Columns (What to Show)"
                icon={Columns}
                items={columns}
                onDrop={(e) => handleDrop(e, 'columns')}
                onRemove={removeColumn}
                isDragOver={dragOverZone === 'columns'}
                onDragOver={(e) => handleDragOver(e, 'columns')}
                onDragLeave={handleDragLeave}
                emptyText="Drag fields here to select columns"
                color="violet"
                renderItem={(item, idx, remove) => (
                  <ColumnItem key={idx} item={item} index={idx} onRemove={remove} />
                )}
              />
            )}

            {/* Filters Zone */}
            <DropZone
              title="Filters (Conditions)"
              icon={Filter}
              items={filters}
              onDrop={(e) => handleDrop(e, 'filters')}
              onRemove={removeFilter}
              isDragOver={dragOverZone === 'filters'}
              onDragOver={(e) => handleDragOver(e, 'filters')}
              onDragLeave={handleDragLeave}
              emptyText="Drag fields here to add filter conditions"
              color="pink"
              renderItem={(item, idx, remove) => (
                <FilterItem 
                  key={idx} 
                  item={item} 
                  index={idx} 
                  onRemove={remove}
                  onUpdate={updateFilter}
                  operators={schema?.operators || {}}
                />
              )}
            />

            {/* Group By Zone */}
            {outputType === 'aggregate' && (
              <DropZone
                title="Group By (Dimensions)"
                icon={Layers}
                items={groupBy}
                onDrop={(e) => handleDrop(e, 'groupBy')}
                onRemove={removeGroupBy}
                isDragOver={dragOverZone === 'groupBy'}
                onDragOver={(e) => handleDragOver(e, 'groupBy')}
                onDragLeave={handleDragLeave}
                emptyText="Drag fields here to group results"
                color="cyan"
                renderItem={(item, idx, remove) => (
                  <GroupByItem key={idx} item={item} index={idx} onRemove={remove} />
                )}
              />
            )}

            {/* Sort Configuration */}
            {outputType !== 'count' && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Sort By:</span>
                    </div>
                    <Select 
                      value={sortBy.field || 'none'} 
                      onValueChange={(v) => setSortBy({ ...sortBy, field: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger className="w-48 h-8">
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Default</SelectItem>
                        {columns.map(c => (
                          <SelectItem key={c.field_id} value={c.field_id}>{c.label}</SelectItem>
                        ))}
                        <SelectItem value="ltv">Lifetime Value</SelectItem>
                        <SelectItem value="visit_count">Visit Count</SelectItem>
                        <SelectItem value="count">Count</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSortBy({ ...sortBy, desc: !sortBy.desc })}
                      className="gap-1"
                    >
                      {sortBy.desc ? '↓ Desc' : '↑ Asc'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Execute Buttons */}
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => executeReport(true)} 
              disabled={executing}
              variant="outline"
              className="flex-1 gap-2"
            >
              {executing && previewMode ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
              Preview (10 rows)
            </Button>
            <Button 
              onClick={() => executeReport(false)} 
              disabled={executing}
              className="flex-1 gap-2 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
            >
              {executing && !previewMode ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Run Report
            </Button>
          </div>
        </div>

        {/* Results Panel - Right Side */}
        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!results ? (
                <div className="py-12 text-center text-muted-foreground">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">Build your query and click Run Report to see results</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Count Display */}
                  <div className="p-4 rounded-lg bg-gradient-to-br from-violet-500/10 to-purple-500/5 border border-violet-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Total Count</span>
                      {results.has_more && (
                        <Badge variant="outline" className="text-xs">
                          Showing {results.rows?.length} of {results.count}
                        </Badge>
                      )}
                    </div>
                    <p className="text-3xl font-bold text-violet-500">
                      {results.count?.toLocaleString()}
                    </p>
                    {results.count > 500 && results.type === 'list' && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-amber-500">
                        <AlertCircle className="w-3 h-3" />
                        Large dataset - export as CSV recommended
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  {results.count > 500 && (
                    <Button 
                      onClick={exportCSV} 
                      className="w-full gap-2"
                      variant="outline"
                    >
                      <Download className="w-4 h-4" />
                      Export Full Dataset as CSV
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Results Table */}
      {results?.rows?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Table className="w-4 h-4" />
                Data Preview
                <Badge variant="secondary">{results.rows.length} rows</Badge>
              </CardTitle>
              {results.has_more && (
                <Button variant="outline" size="sm" onClick={exportCSV} className="gap-2">
                  <Download className="w-4 h-4" />
                  Export All ({results.count})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <ResultsTable data={results} columns={columns} />
          </CardContent>
        </Card>
      )}

      {/* Saved Reports Panel */}
      <SavedReportsPanel
        reports={savedReports}
        onLoad={loadReport}
        onDelete={deleteReport}
        isOpen={showSavedReports}
        onClose={() => setShowSavedReports(false)}
      />
    </div>
  );
}
